import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getRichArticle } from '@/mocks/richArticles';
import type { RichBlock, SupportedLang } from '@/types/article';

const HOME_LABEL: Record<SupportedLang, string> = {
  en: 'Home',
  fr: 'Accueil',
  da: 'Hjem',
  de: 'Startseite',
};

const TOC_LABEL: Record<SupportedLang, string> = {
  en: 'Table of Contents',
  fr: 'Sommaire',
  da: 'Indhold',
  de: 'Inhalt',
};

const FAQ_LABEL: Record<SupportedLang, string> = {
  en: 'Frequently Asked Questions',
  fr: 'Questions fr\u00e9quentes',
  da: 'Ofte stillede sp\u00f8rgsm\u00e5l',
  de: 'H\u00e4ufige Fragen',
};

const RELATED_LABEL: Record<SupportedLang, string> = {
  en: 'Related Articles',
  fr: 'Articles li\u00e9s',
  da: 'Relaterede artikler',
  de: 'Verwandte Artikel',
};

const PROS_LABEL: Record<SupportedLang, string> = {
  en: 'Pros',
  fr: 'Avantages',
  da: 'Fordele',
  de: 'Vorteile',
};

const CONS_LABEL: Record<SupportedLang, string> = {
  en: 'Cons',
  fr: 'Inconv\u00e9nients',
  da: 'Ulemper',
  de: 'Nachteile',
};

const UPDATED_LABEL: Record<SupportedLang, string> = {
  en: 'Updated',
  fr: 'Mis \u00e0 jour',
  da: 'Opdateret',
  de: 'Aktualisiert',
};

const READ_LABEL: Record<SupportedLang, string> = {
  en: 'min read',
  fr: 'min de lecture',
  da: 'min l\u00e6sning',
  de: 'Min. Lesezeit',
};

function normalizeLang(lng: string): SupportedLang {
  const base = lng.split('-')[0];
  if (base === 'fr' || base === 'da' || base === 'de') return base;
  return 'en';
}

function Block({ block, lang }: { block: RichBlock; lang: SupportedLang }) {
  switch (block.type) {
    case 'p':
      return <p className="text-base text-foreground-700 leading-relaxed mb-4">{block.text}</p>;
    case 'h2':
      return (
        <h2 id={block.id} className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4 scroll-mt-28">
          {block.text}
        </h2>
      );
    case 'h3':
      return <h3 className="font-heading text-lg font-semibold text-foreground-900 mt-6 mb-3">{block.text}</h3>;
    case 'list':
      return (
        <ul className="space-y-2 mb-6 text-sm text-foreground-700">
          {block.items.map((item, k) => (
            <li key={k} className="flex items-start gap-2">
              <i className="ri-check-line text-accent-500 mt-0.5" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    case 'ol':
      return (
        <div className="bg-background-100 rounded-lg p-4 md:p-6 mb-6 border border-background-200/70">
          <ol className="space-y-3 text-sm text-foreground-700">
            {block.items.map((item, k) => (
              <li key={k} className="flex items-start gap-3">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold flex-shrink-0">
                  {k + 1}
                </span>
                <span>{item}</span>
              </li>
            ))}
          </ol>
        </div>
      );
    case 'table':
      return (
        <div className="overflow-x-auto mb-6">
          <table className="w-full text-sm border border-background-200/70 rounded-lg">
            <thead>
              <tr className="bg-background-100">
                {block.headers.map((h, k) => (
                  <th key={k} className="px-4 py-3 text-left font-medium text-foreground-700 whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200/50">
              {block.rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td key={c} className={`px-4 py-3 text-foreground-600 ${c === 0 ? 'font-medium text-foreground-700' : ''}`}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'proscons':
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div className="rounded-lg bg-accent-50 border border-accent-200/60 p-4">
            <p className="flex items-center gap-2 text-sm font-semibold text-accent-800 mb-3">
              <i className="ri-thumb-up-line" /> {PROS_LABEL[lang]}
            </p>
            <ul className="space-y-2 text-sm text-foreground-700">
              {block.pros.map((p, k) => (
                <li key={k} className="flex items-start gap-2">
                  <i className="ri-add-line text-accent-600 mt-0.5" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-lg bg-secondary-50 border border-secondary-200/60 p-4">
            <p className="flex items-center gap-2 text-sm font-semibold text-secondary-800 mb-3">
              <i className="ri-thumb-down-line" /> {CONS_LABEL[lang]}
            </p>
            <ul className="space-y-2 text-sm text-foreground-700">
              {block.cons.map((c, k) => (
                <li key={k} className="flex items-start gap-2">
                  <i className="ri-subtract-line text-secondary-600 mt-0.5" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      );
    case 'callout':
      return (
        <div className="bg-primary-50 rounded-lg p-4 md:p-5 border border-primary-200/50 mb-6">
          <p className="text-sm text-primary-800 font-semibold mb-1.5 flex items-center gap-2">
            <i className="ri-lightbulb-line" /> {block.title}
          </p>
          <p className="text-sm text-primary-700 leading-relaxed">{block.text}</p>
        </div>
      );
    case 'quote':
      return (
        <blockquote className="border-l-4 border-accent-400 pl-4 py-2 my-6 text-base text-foreground-700 italic">
          {block.text}
        </blockquote>
      );
    default:
      return null;
  }
}

export default function RichArticlePage({ slug }: { slug: string }) {
  const { t, i18n } = useTranslation('home');
  const lang = normalizeLang(i18n.language);
  const article = getRichArticle(slug);
  const [activeToc, setActiveToc] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const content = article?.content[lang];

  const tocItems = useMemo(() => {
    if (!content) return [];
    return content.blocks.filter((b): b is Extract<RichBlock, { type: 'h2' }> => b.type === 'h2');
  }, [content]);

  useEffect(() => {
    if (tocItems.length > 0) setActiveToc(tocItems[0].id);
  }, [tocItems]);

  useEffect(() => {
    if (content) document.title = content.metaTitle;
  }, [content]);

  if (!article || !content) return null;

  const scrollTo = (id: string) => {
    setActiveToc(id);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <div className="container-main pt-28 md:pt-32 pb-4">
        <nav className="flex items-center gap-2 text-sm text-foreground-400 flex-wrap">
          <a href="/" className="hover:text-primary-600 transition-colors font-medium">
            {HOME_LABEL[lang]}
          </a>
          <i className="ri-arrow-right-s-line text-xs" />
          <span className="text-foreground-600">{content.title}</span>
        </nav>
      </div>

      <div className="container-main pb-16">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* Sidebar TOC */}
          <aside className="lg:w-64 lg:flex-shrink-0">
            <div className="sticky top-24">
              <p className="text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-3">{TOC_LABEL[lang]}</p>
              <ul className="space-y-1">
                {tocItems.map((item) => (
                  <li key={item.id}>
                    <button
                      onClick={() => scrollTo(item.id)}
                      className={`block w-full text-left px-3 py-2 rounded-md text-sm transition-colors cursor-pointer ${
                        activeToc === item.id
                          ? 'bg-primary-50 text-primary-700 font-medium'
                          : 'text-foreground-600 hover:bg-background-100'
                      }`}
                    >
                      {item.text}
                    </button>
                  </li>
                ))}
              </ul>

              <div className="mt-6 p-4 rounded-lg bg-accent-50 border border-accent-200/50">
                <a href="/" className="block text-sm text-accent-600 hover:text-accent-700 mb-1">
                  ← {HOME_LABEL[lang]}
                </a>
                {article.related.slice(0, 3).map((r) => (
                  <a key={r.slug} href={`/${r.slug}`} className="block text-sm text-accent-600 hover:text-accent-700 mb-1">
                    {r.label}
                  </a>
                ))}
              </div>

              <div className="mt-4 p-4 rounded-lg bg-primary-50 border border-primary-200/50">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="btn-primary w-full text-sm justify-center"
                >
                  {t('cta.main_button')}
                </a>
              </div>
            </div>
          </aside>

          {/* Article Content */}
          <article className="flex-1 min-w-0">
            <header className="mb-8">
              <span className="inline-block px-2.5 py-1 rounded text-xs font-medium bg-primary-100 text-primary-700 mb-3">
                {article.tag}
              </span>
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4">
                {content.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-400 flex-wrap">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line" />
                  {UPDATED_LABEL[lang]} {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line" />
                  {article.readTime} {READ_LABEL[lang]}
                </span>
              </div>
            </header>

            <div className="max-w-none">
              <div className="rounded-lg overflow-hidden mb-8">
                <img src={article.image} alt={content.title} className="w-full h-64 md:h-80 object-cover object-top" />
              </div>

              <p className="text-base text-foreground-700 leading-relaxed mb-6 font-medium">{content.intro}</p>

              {content.blocks.map((block, i) => (
                <Block key={i} block={block} lang={lang} />
              ))}

              {/* FAQ */}
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">{FAQ_LABEL[lang]}</h2>
              <div className="space-y-4 mb-8">
                {content.faqs.map((faq, i) => (
                  <div key={i} className="bg-background-100 rounded-lg p-4 border border-background-200/50">
                    <p className="font-medium text-sm text-foreground-900 mb-2">{faq.q}</p>
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <div className="bg-primary-50 rounded-lg p-6 md:p-8 border border-primary-200/50 text-center mt-10">
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">{t('cta.ready_title')}</h3>
                <p className="text-sm text-foreground-600 mb-4 max-w-md mx-auto">{t('cta.ready_desc')}</p>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="btn-primary inline-flex"
                >
                  {t('cta.visit_main')}
                  <i className="ri-external-link-line ml-1" />
                </a>
              </div>

              {/* Related links */}
              <div className="mt-10 pt-6 border-t border-background-200/50">
                <p className="text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-3">{RELATED_LABEL[lang]}</p>
                <div className="flex flex-wrap gap-2">
                  <a
                    href="/"
                    className="px-3 py-1.5 rounded-md bg-background-100 border border-background-200/50 text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-700 transition-colors"
                  >
                    {HOME_LABEL[lang]}
                  </a>
                  {article.related.map((r) => (
                    <a
                      key={r.slug}
                      href={`/${r.slug}`}
                      className="px-3 py-1.5 rounded-md bg-background-100 border border-background-200/50 text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-700 transition-colors"
                    >
                      {r.label}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </article>
        </div>
      </div>

      <Footer />
    </div>
  );
}